// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { InfoOutlined as InfoOutlinedIcon } from '@/constants/hardfin-standard-icons';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';

interface SummaryCardProps {
  children: React.ReactNode;
  size?: 'default' | 'compact';
}

export function SummaryCard(props: SummaryCardProps) {
  const padding = props.size === 'compact' ? 2 : 3;

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ p: padding, '&:last-child': { pb: padding } }}>
        {props.children}
      </CardContent>
    </Card>
  );
}

interface LabelWithTooltipProps {
  label: string;
  tooltip?: string;
  typographyVariant: 'body1' | 'body2';
  sx?: SxProps<Theme>;
}

// Renders the label text + (optional) info icon as a single hover target.
// When a tooltip is provided, both the text and the icon are wrapped in
// the Tooltip so hover anywhere on the label area surfaces the tooltip —
// the icon alone was too small a hit target. The hover target uses
// inline-flex so it sizes to text + icon width only, rather than
// stretching across the full row.
function LabelWithTooltip(props: LabelWithTooltipProps) {
  const content = (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      sx={{
        display: 'inline-flex',
        ...(props.tooltip ? { cursor: 'help' } : {}),
        ...props.sx,
      }}
    >
      <Typography
        component="span"
        variant={props.typographyVariant}
        color="text.secondary"
      >
        {props.label}
      </Typography>
      {props.tooltip && (
        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      )}
    </Stack>
  );
  if (!props.tooltip) {
    return content;
  }
  return (
    <Tooltip arrow title={props.tooltip} placement="top">
      {content}
    </Tooltip>
  );
}

interface PrimaryMetricProps {
  label: string;
  size?: 'default' | 'compact';
  // The metric value, rendered as a styled Typography. Required
  // unless `valueNode` is provided (in which case the caller renders
  // the value entirely).
  value?: string;
  color?: string;
  isLoading?: boolean;
  tooltip?: string;
  // Optional italic right-aligned subtitle rendered beneath the primary
  // value (e.g. "12 invoices"). Always renders in text.secondary — the
  // caption is a muted secondary fact and should never compete with the
  // primary value for color emphasis. Use for a single short supplementary
  // fact tied directly to the primary metric. For multi-row breakdowns,
  // use MetricTable / MetricRow inside a MetricDivider instead.
  caption?: string;
  // Optional explanations with an info icon after the text. The icon and text
  // share a right-aligned hover target.
  valueTooltip?: React.ReactNode;
  captionTooltip?: React.ReactNode;
  // Caller-rendered replacement for the value Typography (typically a
  // Link wrapping a styled value, for deep-linkable hub cards). When
  // provided, `value` and `color` are ignored. Rendered inside a
  // right-aligned flex container so the click target is only as wide as
  // the value, not the full row.
  valueNode?: React.ReactNode;
}

function MetricTooltip(props: {
  tooltip?: React.ReactNode;
  children: React.ReactNode;
}) {
  if (!props.tooltip) {
    return props.children;
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Tooltip arrow title={props.tooltip} placement="top">
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ display: 'inline-flex', cursor: 'help' }}
        >
          {props.children}
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        </Stack>
      </Tooltip>
    </Box>
  );
}

export function PrimaryMetric(props: PrimaryMetricProps) {
  return (
    <Box>
      <LabelWithTooltip
        label={props.label}
        tooltip={props.tooltip}
        typographyVariant="body1"
        sx={{ mb: 1 }}
      />
      {props.isLoading ? (
        <Skeleton variant="text" width={120} height={48} />
      ) : (
        <MetricTooltip tooltip={props.valueTooltip}>
          {props.valueNode !== undefined ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {props.valueNode}
            </Box>
          ) : (
            <Typography
              variant={props.size === 'compact' ? 'h5' : 'h3'}
              sx={{
                color: props.color || 'text.primary',
                fontWeight: 600,
                textAlign: 'right',
              }}
            >
              {props.value ?? ''}
            </Typography>
          )}
        </MetricTooltip>
      )}
      {(props.caption !== undefined || props.isLoading) &&
        (props.isLoading ? (
          <Skeleton
            variant="text"
            width={64}
            height={20}
            sx={{ ml: 'auto', mt: 0.5 }}
          />
        ) : (
          <MetricTooltip tooltip={props.captionTooltip}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'right', fontStyle: 'italic', mt: 0.5 }}
            >
              {props.caption}
            </Typography>
          </MetricTooltip>
        ))}
    </Box>
  );
}

interface MetricRowProps {
  label: string;
  value: string;
  color?: string;
  isLoading?: boolean;
  tooltip?: string;
}

export function MetricRow(props: MetricRowProps) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <LabelWithTooltip
        label={props.label}
        tooltip={props.tooltip}
        typographyVariant="body2"
      />
      {props.isLoading ? (
        <Skeleton variant="text" width={80} height={24} />
      ) : (
        <Typography
          variant="body1"
          sx={{
            color: props.color || 'text.primary',
            fontWeight: 500,
            textAlign: 'right',
          }}
        >
          {props.value}
        </Typography>
      )}
    </Stack>
  );
}

interface MetricDividerProps {
  children: React.ReactNode;
}

export function MetricDivider(props: MetricDividerProps) {
  return (
    <Box
      sx={{
        mt: 2,
        pt: 2,
        borderTop: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {props.children}
    </Box>
  );
}

export interface MetricTableRow {
  label: string;
  count: string;
  amount: string;
  color?: string;
}

interface MetricTableProps {
  rows: MetricTableRow[];
  isLoading?: boolean;
  amountHeader?: string;
}

export function MetricTable(props: MetricTableProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        columnGap: 2,
        rowGap: 0.75,
        alignItems: 'center',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        Status
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: 'right' }}
      >
        Count
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: 'right' }}
      >
        {props.amountHeader ?? 'Amount'}
      </Typography>
      {props.rows.map((row) => (
        <MetricTableRowCells
          key={row.label}
          row={row}
          isLoading={props.isLoading}
        />
      ))}
    </Box>
  );
}

interface MetricTableRowCellsProps {
  row: MetricTableRow;
  isLoading?: boolean;
}

function MetricTableRowCells(props: MetricTableRowCellsProps) {
  const valueColor = props.row.color || 'text.primary';
  return (
    <>
      <Typography variant="body2" color="text.secondary">
        {props.row.label}
      </Typography>
      {props.isLoading ? (
        <Skeleton variant="text" width={40} height={24} />
      ) : (
        <Typography
          variant="body2"
          sx={{ color: valueColor, fontWeight: 500, textAlign: 'right' }}
        >
          {props.row.count}
        </Typography>
      )}
      {props.isLoading ? (
        <Skeleton variant="text" width={80} height={24} />
      ) : (
        <Typography
          variant="body2"
          sx={{ color: valueColor, fontWeight: 500, textAlign: 'right' }}
        >
          {props.row.amount}
        </Typography>
      )}
    </>
  );
}
